import { Avatar, Colors, Text } from '@amelio/ui-library';

import { useAuthorSignature } from '@/hooks/useAuthorSignature';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';

export interface AuthorSignaturePreviewProps {
    /** The respondent's full name, or null when it is not known, as in a preview. */
    authorName: string | null;
    authorPictureUrl?: string | null;
    isAnonymous: boolean;
    /** What leads the signature. */
    label: string;
    /** What follows the signature, such as what it leaves out. */
    note?: string;
    className?: string;
    dataTestId?: string;
}

/**
 * Shows a respondent how what they write will be signed, by the very rule that signs it in the
 * results and in their thread: the neutral label of an anonymous feedback, their name and photo
 * otherwise.
 */
export const AuthorSignaturePreview = ({
    authorName,
    authorPictureUrl,
    isAnonymous,
    label,
    note,
    className = '',
    dataTestId = 'author-signature-preview',
}: AuthorSignaturePreviewProps) => {
    const { t } = useAmelioTranslations();
    const signature = useAuthorSignature({
        authorName,
        authorPictureUrl,
        isAnonymousRespondent: isAnonymous,
        nameWhenUnknown: t('answer.feedbackPrivacy.named.yourName'),
    });

    return (
        <div
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}
            data-testid={dataTestId}
        >
            <Text color={Colors.BASE_600}>{label}</Text>

            <div className={'flex items-center gap-2'}>
                <Avatar
                    alt={signature.name}
                    src={signature.pictureUrl}
                    initials={signature.initials}
                    size={'3xs'}
                />

                <Text
                    weight={'semibold'}
                    color={Colors.BASE}
                >
                    {signature.name}
                </Text>
            </div>

            {note && <Text color={Colors.BASE_600}>{note}</Text>}
        </div>
    );
};
